import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Footer } from '@/components/layout/Footer';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { UserIcon, ShoppingBagIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { useCart } from '@/hooks/useCart';
import { CartSidebar } from '@/components/cart/CartSidebar';
import { categoryService } from '@/services/category.service';
import { CategoryWithSubCategories } from '@/types/category.types';
import { homeBoxesService, homeComingBoxesService, HomeBox } from '@/services/home-boxes.service';
import { useFAQs } from '@/hooks/useFAQs';
import { useTestimonials } from '@/hooks/useTestimonials';
import { useAuth } from '@/context/AuthContext';
import { BrandLogo } from '@/components/common/BrandLogo';

export const Home = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { cartCount } = useCart();
  const { user } = useAuth();
  const [showBackToTop, setShowBackToTop] = useState(false);
  type HomeCategoryNavItem = {
    key: string;
    label: string;
    slug: string;
    isSub: boolean;
  };
  const [activeNavKey, setActiveNavKey] = useState('');
  const [categoryNavItems, setCategoryNavItems] = useState<HomeCategoryNavItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [campaignCards, setCampaignCards] = useState<HomeBox[]>([]);
  const [comingSoonCards, setComingSoonCards] = useState<HomeBox[]>([]);
  const [activeFaqIndex, setActiveFaqIndex] = useState<number | null>(0);
  const categoryNavWrapRef = useRef<HTMLDivElement>(null);
  const categoryNavRef = useRef<HTMLElement>(null);
  const categoryNavTriggerTopRef = useRef(0);
  const [isCategoryNavFixed, setIsCategoryNavFixed] = useState(false);
  const [categoryNavHeight, setCategoryNavHeight] = useState(0);
  const { faqs, loading: loadingFaqs } = useFAQs();
  const { testimonials, loading: loadingTestimonials } = useTestimonials();

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 420);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const wrap = categoryNavWrapRef.current;
    const nav = categoryNavRef.current;
    if (!wrap || !nav) return;

    const updateCategoryNavMetrics = () => {
      setCategoryNavHeight(nav.offsetHeight);
      categoryNavTriggerTopRef.current = wrap.offsetTop;
      setIsCategoryNavFixed(window.scrollY >= categoryNavTriggerTopRef.current);
    };

    updateCategoryNavMetrics();

    const handleScroll = () => {
      setIsCategoryNavFixed(window.scrollY >= categoryNavTriggerTopRef.current);
    };

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateCategoryNavMetrics)
      : null;

    resizeObserver?.observe(nav);
    window.addEventListener('resize', updateCategoryNavMetrics);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateCategoryNavMetrics);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [categoryNavItems.length]);

  useEffect(() => {
    const loadCategories = async () => {
      const fallbackLabel = t('homeLanding.categoriesFallback');
      const slugify = (value: string) =>
        value
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');

      const buildNav = (list: CategoryWithSubCategories[]): HomeCategoryNavItem[] => {
        const items: HomeCategoryNavItem[] = [];
        for (const cat of list.filter((c) => c.is_active)) {
          if (!cat.name?.trim()) continue;
          items.push({
            key: `c-${cat.id}`,
            label: cat.name,
            slug: (cat.slug && cat.slug.trim()) || slugify(cat.name),
            isSub: false,
          });
          for (const sub of cat.sub_categories || []) {
            if (!sub.is_active || !sub.name?.trim()) continue;
            items.push({
              key: `s-${sub.id}`,
              label: sub.name,
              slug: (sub.slug && sub.slug.trim()) || slugify(sub.name),
              isSub: true,
            });
          }
        }
        return items;
      };

      try {
        const apiCategories = await categoryService.getAll();
        const items = buildNav(apiCategories);

        if (items.length > 0) {
          setCategoryNavItems(items);
          setActiveNavKey((prev) => prev || items[0].key);
        } else {
          setCategoryNavItems([
            { key: 'fallback', label: fallbackLabel, slug: slugify(fallbackLabel), isSub: false },
          ]);
          setActiveNavKey('fallback');
        }
      } catch (error) {
        console.error('Erreur chargement catégories home:', error);
        const slug = slugify(fallbackLabel);
        setCategoryNavItems([{ key: 'fallback', label: fallbackLabel, slug, isSub: false }]);
        setActiveNavKey('fallback');
      }
    };

    loadCategories();
  }, [t, i18n.language]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const query = searchTerm.trim();
    navigate(query ? `/boutique?search=${encodeURIComponent(query)}` : '/boutique');
  };

  useEffect(() => {
    const loadHomeBoxes = async () => {
      try {
        const [collectionData, comingSoonData] = await Promise.all([
          homeBoxesService.getPublic(),
          homeComingBoxesService.getPublic(),
        ]);
        setCampaignCards(collectionData);
        setComingSoonCards(comingSoonData);
      } catch (error) {
        console.error('Erreur chargement home boxes:', error);
        setCampaignCards([]);
        setComingSoonCards([]);
      }
    };

    loadHomeBoxes();
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="new-home-page">
      <div className="top-bar-nav">
        <div className="top-bar-content">
          <div className="top-bar-left">
            <span>{t('navbar.welcome')}</span>
          </div>
          <div className="top-bar-links">
            <Link to="/order-tracking">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" style={{ width: '16px', height: '16px', marginRight: '6px', display: 'inline-block', verticalAlign: 'middle' }}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
              </svg>
              <span className="top-bar-link-text">{t('navbar.trackOrder')}</span>
            </Link>
            <div className="top-bar-socials" aria-label={t('homeLanding.socialNetworksAria')}>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.75A4 4 0 0 0 3.75 7.75v8.5a4 4 0 0 0 4 4h8.5a4 4 0 0 0 4-4v-8.5a4 4 0 0 0-4-4h-8.5Zm8.75 1.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 1.75a3.25 3.25 0 1 0 0 6.5 3.25 3.25 0 0 0 0-6.5Z" />
                </svg>
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13.5 21v-7h2.3l.45-3h-2.75V9.4c0-.82.4-1.62 1.68-1.62h1.3V5.2s-1.18-.2-2.3-.2c-2.35 0-3.88 1.42-3.88 4v2h-2.6v3h2.6v7h3.2Z" />
                </svg>
              </a>
              <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14.5 3h2.1c.2 1.2.9 2.3 1.9 3 .9.6 2 .9 3.1 1v2.2c-1.3 0-2.6-.3-3.8-.9v6.1a5.4 5.4 0 1 1-4.7-5.3v2.3a3.1 3.1 0 1 0 2.4 3V3Z" />
                </svg>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.2 8.4a1.9 1.9 0 1 1 0-3.8 1.9 1.9 0 0 1 0 3.8ZM4.6 9.8h3.2V20H4.6V9.8Zm5.2 0h3v1.4h.1c.4-.8 1.4-1.6 3-1.6 3.2 0 3.8 2.1 3.8 4.8V20h-3.2v-4.8c0-1.2 0-2.7-1.6-2.7s-1.9 1.3-1.9 2.6V20H9.8V9.8Z" />
                </svg>
              </a>
            </div>
            <div className="top-bar-language-switcher">
              <span className="top-bar-language-label">{t('app.language')}:</span>
              <LanguageSwitcher className="language-switcher-inline" />
            </div>
          </div>
        </div>
      </div>

      <header className="home-top-header">
        <div className="home-top-header-content">
          <Link to="/" className="home-brand-logo" aria-label={t('homeLanding.brandLogoAria')}>
            <BrandLogo size="home" />
          </Link>
          <div className="home-search-area">
            <form className="home-search-wrap" onSubmit={handleSearch}>
              <input
                type="search"
                className="home-search-input"
                placeholder={t('homeLanding.searchPlaceholder')}
                aria-label={t('homeLanding.searchAria')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </form>
            <div className="home-quick-links">
              <Link to="/about" className="home-quick-link">{t('homeLanding.quickAbout')}</Link>
              <Link to="/contact" className="home-quick-link">{t('homeLanding.quickContact')}</Link>
            </div>
          </div>
          <div className="home-action-icons">
            {user ? (
              <button
                type="button"
                className="home-icon-button"
                aria-label={t('homeLanding.ariaDashboard')}
                onClick={() => navigate('/admin/dashboard')}
              >
                <Squares2X2Icon className="home-action-icon" />
              </button>
            ) : (
              <button type="button" className="home-icon-button" aria-label={t('homeLanding.ariaLogin')} onClick={() => navigate('/login')}>
                <UserIcon className="home-action-icon" />
              </button>
            )}
            <button type="button" className="home-icon-button home-cart-button" aria-label={t('homeLanding.ariaCart')} onClick={() => setIsCartOpen(true)}>
              <ShoppingBagIcon className="home-action-icon" />
              {cartCount > 0 && <span className="home-cart-count">{cartCount}</span>}
            </button>
          </div>
        </div>
      </header>

      <div
        ref={categoryNavWrapRef}
        className="home-category-nav-wrap"
        style={isCategoryNavFixed ? { height: categoryNavHeight } : undefined}
      >
        <nav
          ref={categoryNavRef}
          className={`home-category-nav ${isCategoryNavFixed ? 'is-fixed' : ''}`}
          aria-label={t('homeLanding.categoriesNavAria')}
        >
          <div className="home-category-scroll">
            {categoryNavItems.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`home-category-pill ${item.isSub ? 'home-category-pill--sub' : ''} ${activeNavKey === item.key ? 'is-active' : ''}`}
                onClick={() => {
                  setActiveNavKey(item.key);
                  navigate(`/categorie/${item.slug}`);
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </nav>
      </div>

      <section className="home-hero-showcase" aria-labelledby="home-hero-title">
        <div className="home-hero-overlay" />
        <div className="home-hero-content">
          <h2 id="home-hero-title" className="gtitle home-hero-main-title">
            <span className="gtitle-light">{t('homeLanding.heroWelcomeLight')}</span>
            <span className="gtitle-strong">{t('homeLanding.heroWelcomeStrong')}</span>
          </h2>
          <p className="home-hero-subtitle">{t('homeLanding.heroSubtitle')}</p>
        </div>
      </section>

      <main className="home-main-content">
        {campaignCards.length > 0 && (
        <section className="home-content-block" aria-labelledby="new-releases-title">
          <h4 id="new-releases-title" className="gtitle">
            <span className="gtitle-light">{t('homeLanding.collectionLight')}</span>
            <span className="gtitle-strong">{t('homeLanding.collectionStrong')}</span>
          </h4>
          <div className="home-campaign-grid">
            {campaignCards.map((card) => (
              <article
                key={card.id}
                className="home-campaign-card"
                onClick={() => navigate(`/home-box/${card.slug}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/home-box/${card.slug}`);
                  }
                }}
              >
                {card.background_image_url ? (
                  <img src={card.background_image_url} alt={card.title || 'Collection'} className="home-campaign-image" />
                ) : (
                  <div className="home-campaign-image home-campaign-image-placeholder" />
                )}
                {card.badge && <span className="home-campaign-badge">{card.badge}</span>}
                {(card.title?.trim() || card.description?.trim()) && (
                  <div className="home-campaign-content">
                    {card.title?.trim() ? <h3>{card.title}</h3> : null}
                    {card.description?.trim() ? <p>{card.description}</p> : null}
                  </div>
                )}
              </article>
            ))}
        </div>
      </section>
        )}

        {comingSoonCards.length > 0 && (
        <section className="home-content-block" aria-labelledby="coming-soon-title">
          <h4 id="coming-soon-title" className="gtitle">
            <span className="gtitle-light">{t('homeLanding.comingSoonLight')}</span>
            <span className="gtitle-strong">{t('homeLanding.comingSoonStrong')}</span>
          </h4>
          <div className="home-campaign-grid">
            {comingSoonCards.map((card) => (
              <article key={card.id} className="home-coming-soon-card">
                {card.background_image_url ? (
                  <img src={card.background_image_url} alt={card.title || 'Collection'} className="home-campaign-image" />
                ) : (
                  <div className="home-campaign-image home-campaign-image-placeholder" />
                )}
                <div className="home-coming-soon-overlay">
                  <div className="home-coming-soon-overlay-content">
                    {card.title?.trim() ? (
                      <h3 className="home-coming-soon-title">{card.title}</h3>
                    ) : null}
                    {!user && (
                      <Link to="/register" className="home-coming-soon-cta home-primary-cta">
                        {t('authPages.registerButton')}
                      </Link>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
        )}

        <section className="home-engagement-block" aria-labelledby="engagement-title">
          <div className="home-engagement-inner">
            <h2 id="engagement-title" className="gtitle">
              <span className="gtitle-light">{t('homeLanding.engageLight')}</span>
              <span className="gtitle-strong">{t('homeLanding.engageStrong')}</span>
            </h2>
            <p>{t('homeLanding.engageText')}</p>
            <button type="button" className="home-primary-cta" onClick={() => navigate('/login')}>
              {t('homeLanding.engageLogin')}
            </button>
            <div className="home-reassurance-list">
              <span>{t('homeLanding.trustSecurePayment')}</span>
              <span>{t('homeLanding.trustFastDelivery')}</span>
              <span>{t('homeLanding.trustSupport')}</span>
            </div>
          </div>
        </section>

        <section className="home-faq-block" aria-labelledby="faq-title">
          <h4 id="faq-title" className="gtitle">
            <span className="gtitle-light">{t('homeLanding.faqLight')}</span>
            <span className="gtitle-strong">{t('homeLanding.faqStrong')}</span>
          </h4>
          <div className="home-faq-list">
            {loadingFaqs ? (
              <p className="home-faq-empty">{t('homeLanding.faqLoading')}</p>
            ) : faqs.length === 0 ? (
              <p className="home-faq-empty">{t('homeLanding.faqEmpty')}</p>
            ) : (
              faqs.slice(0, 6).map((faq, index) => (
                <article key={faq.id} className={`home-faq-item ${activeFaqIndex === index ? 'is-open' : ''}`}>
                  <button
                    type="button"
                    className="home-faq-question"
                    onClick={() => setActiveFaqIndex((prev) => (prev === index ? null : index))}
                  >
                    <span>{faq.question}</span>
                    <span className="home-faq-toggle">{activeFaqIndex === index ? '-' : '+'}</span>
                  </button>
                  {activeFaqIndex === index && <p className="home-faq-answer">{faq.answer}</p>}
                </article>
              ))
            )}
          </div>
        </section>

        <section className="home-testimonials-block" aria-labelledby="testimonials-title">
          <h4 id="testimonials-title" className="gtitle">
            <span className="gtitle-light">{t('homeLanding.testimonialsLight')}</span>
            <span className="gtitle-strong">{t('homeLanding.testimonialsStrong')}</span>
          </h4>
          <div className="home-testimonials-grid">
            {loadingTestimonials ? (
              <p className="home-faq-empty">{t('homeLanding.testimonialsLoading')}</p>
            ) : testimonials.length === 0 ? (
              <p className="home-faq-empty">{t('testimonials.empty')}</p>
            ) : (
              testimonials.slice(0, 6).map((testimonial) => (
                <article key={testimonial.id} className="home-testimonial-card">
                  <div className="home-testimonial-header">
                    <div className="home-testimonial-avatar" aria-hidden="true">
                      {(testimonial.customer_name || t('homeLanding.defaultClientLabel')).charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3>{testimonial.customer_name || t('homeLanding.defaultClientLabel')}</h3>
                      <p className="home-testimonial-rating">{'★'.repeat(Math.max(1, Math.min(5, testimonial.rating || 5)))}</p>
                    </div>
            </div>
                  <p className="home-testimonial-comment">{testimonial.comment}</p>
                </article>
              ))
            )}
        </div>
      </section>
      </main>

      <Footer />

      {showBackToTop && (
        <button type="button" className="home-back-to-top" onClick={scrollToTop} aria-label={t('homeLanding.backToTopAria')}>
          ↑
        </button>
      )}
      <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  );
};
